import axios from "axios";
import {
  getAccessToken,
  refreshAccessToken,
  isAuthExemptUrl,
  isTokenExpiredError,
} from "./authSession";
import {
  isOrgSsoUrl,
  isSsoRequiredError,
  ssoRequiredOrgId,
  navigateToOrgSsoEnter,
} from "./orgSso";

let interceptorsInstalled = false;

/**
 * Attach Bearer injection + single-flight 401 refresh/retry to default axios.
 * Safe to call once from AuthProvider.
 */
export function setupAxiosAuth() {
  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;

  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const url = config.url || "";
    if (isAuthExemptUrl(url)) {
      return config;
    }

    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      // Prefer live session token over a stale Bearer from React closures
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const url = originalRequest.url || "";
      if (isAuthExemptUrl(url) || originalRequest._authRetry) {
        return Promise.reject(error);
      }

      if (isSsoRequiredError(error) && !isOrgSsoUrl(url) && !originalRequest._ssoHop) {
        originalRequest._ssoHop = true;
        const orgId = ssoRequiredOrgId(error);
        if (orgId) {
          navigateToOrgSsoEnter(orgId);
        }
        return Promise.reject(error);
      }

      if (!isTokenExpiredError(error)) {
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshAccessToken();
        originalRequest._authRetry = true;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );
}
